import { useParams, useNavigate } from "react-router-dom";
import { useLinearProjects, useLinearIssues, type LinearIssue } from "@/hooks/useLinear";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ListTodo,
  Users,
  CalendarDays,
  TrendingUp,
  Layers,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { format, differenceInDays, startOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { StatTile } from "@/components/shared/StatTile";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LINEAR_PRIORITY_TONE } from "@/lib/statusTone";

const CHART_COLORS = [
  "hsl(142.1 76.2% 36.3%)",
  "hsl(217.2 91.2% 59.8%)",
  "hsl(38.3 92% 50.2%)",
  "hsl(262.1 83.3% 57.8%)",
  "hsl(0 84.2% 60.2%)",
  "hsl(215.4 16.3% 46.9%)",
  "hsl(24.6 95% 53.1%)",
  "hsl(47.9 95.8% 53.1%)",
];

const STATE_TYPE_COLORS: Record<string, string> = {
  completed: "hsl(142.1 76.2% 36.3%)",
  started: "hsl(38.3 92% 50.2%)",
  backlog: "hsl(215.4 16.3% 46.9%)",
  unstarted: "hsl(215.4 16.3% 56.9%)",
  cancelled: "hsl(0 84.2% 60.2%)",
  paused: "hsl(24.6 95% 53.1%)",
};

const PRIORITY_COLORS = [
  "hsl(215.4 16.3% 46.9%)",
  "hsl(0 84.2% 60.2%)",
  "hsl(24.6 95% 53.1%)",
  "hsl(47.9 95.8% 53.1%)",
  "hsl(217.2 91.2% 59.8%)",
];

function KpiCard({
  label,
  value,
  sub,
  icon,
  danger,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className={danger ? "text-destructive" : "text-muted-foreground"}>{icon}</span>
        </div>
        <p className={`text-2xl font-bold ${danger ? "text-destructive" : ""}`}>{value}</p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
  height = 280,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  height?: number;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent style={{ height }}>{children}</CardContent>
    </Card>
  );
}

function buildVelocityData(issues: LinearIssue[]) {
  const byWeek: Record<string, number> = {};
  issues.forEach((issue) => {
    const d = new Date(issue.createdAt);
    const weekStart = startOfWeek(d, { weekStartsOn: 1 });
    const key = format(weekStart, "dd/MM", { locale: ptBR });
    byWeek[key] = (byWeek[key] ?? 0) + 1;
  });

  const sorted = Object.entries(byWeek).map(([week, count]) => ({ week, count }));

  const withDates = issues.map((i) => {
    const d = new Date(i.createdAt);
    const weekStart = startOfWeek(d, { weekStartsOn: 1 });
    return { ts: weekStart.getTime(), week: format(weekStart, "dd/MM", { locale: ptBR }) };
  });

  const uniqueWeeks = Array.from(new Map(withDates.map((w) => [w.ts, w])).values())
    .sort((a, b) => a.ts - b.ts)
    .slice(-12)
    .map(({ week }) => ({ week, count: byWeek[week] ?? 0 }));

  return uniqueWeeks;
}

export function LinearProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: projectsData, isLoading: projectsLoading } = useLinearProjects();
  const { data: issuesData, isLoading: issuesLoading, error: issuesError } = useLinearIssues(id);

  const [filterState, setFilterState] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterAssignee, setFilterAssignee] = useState<string>("all");

  const project = projectsData?.projects?.nodes?.find((p) => p.id === id);
  const issues = issuesData?.issues?.nodes ?? [];

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p className="text-sm">Projeto não encontrado.</p>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
        </Button>
      </div>
    );
  }

  // KPI derivations
  const total = issues.length;
  const completed = issues.filter((i) => i.state.type === "completed").length;
  const inProgress = issues.filter((i) => i.state.type === "started").length;
  const backlog = issues.filter((i) => ["backlog", "unstarted"].includes(i.state.type)).length;
  const cancelled = issues.filter((i) => i.state.type === "cancelled").length;
  const progressPct = Math.round((project.progress ?? 0) * 100);

  const daysLeft = project.targetDate
    ? differenceInDays(new Date(project.targetDate), new Date())
    : null;

  // Timeline
  const timeElapsedPct =
    project.startDate && project.targetDate
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round(
              ((new Date().getTime() - new Date(project.startDate).getTime()) /
                (new Date(project.targetDate).getTime() - new Date(project.startDate).getTime())) *
                100,
            ),
          ),
        )
      : null;
  const isBehind = timeElapsedPct !== null && progressPct < timeElapsedPct;

  // Chart: by state
  const stateGroups: Record<string, { count: number; color: string }> = {};
  issues.forEach((i) => {
    const key = i.state.name;
    if (!stateGroups[key]) stateGroups[key] = { count: 0, color: i.state.color };
    stateGroups[key].count += 1;
  });
  const stateData = Object.entries(stateGroups).map(([name, { count, color }]) => ({
    name,
    value: count,
    color,
  }));

  // Chart: by priority
  const PRIORITY_LABELS = ["Sem prioridade", "Urgente", "Alta", "Média", "Baixa"];
  const priorityData = [1, 2, 3, 4, 0]
    .map((p) => ({
      name: PRIORITY_LABELS[p],
      count: issues.filter((i) => i.priority === p).length,
      fill: PRIORITY_COLORS[p],
    }))
    .filter((d) => d.count > 0);

  // Chart: by assignee
  const assigneeGroups: Record<string, number> = {};
  issues.forEach((i) => {
    const name = i.assignee?.name ?? "Sem responsável";
    assigneeGroups[name] = (assigneeGroups[name] ?? 0) + 1;
  });
  const assigneeData = Object.entries(assigneeGroups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Chart: weekly velocity
  const velocityData = buildVelocityData(issues);

  // Unique assignees for filter
  const assignees = Array.from(
    new Set(issues.map((i) => i.assignee?.name ?? "Sem responsável")),
  ).sort();

  // Unique states for filter
  const stateNames = Array.from(new Set(issues.map((i) => i.state.name))).sort();

  // Filtered issues table
  const filtered = issues.filter((i) => {
    if (filterState !== "all" && i.state.name !== filterState) return false;
    if (filterPriority !== "all" && String(i.priority) !== filterPriority) return false;
    if (filterAssignee !== "all" && (i.assignee?.name ?? "Sem responsável") !== filterAssignee)
      return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="mt-0.5 shrink-0"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold leading-tight">{project.name}</h1>
              <Badge
                style={{
                  backgroundColor: project.status.color + "22",
                  color: project.status.color,
                  borderColor: project.status.color + "44",
                }}
                variant="outline"
              >
                {project.status.name}
              </Badge>
            </div>
            {project.description && (
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">{project.description}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {project.startDate && (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  Início: {format(new Date(project.startDate), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              )}
              {project.targetDate && (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  Entrega: {format(new Date(project.targetDate), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              )}
              {project.members.nodes.length > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {project.members.nodes.map((m) => m.name).join(", ")}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" asChild>
          <a href={project.url} target="_blank" rel="noopener noreferrer">
            Abrir no Linear <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile
          label="Total de Issues"
          value={total}
          icon={<ListTodo className="h-4 w-4" />}
          foot={issuesLoading ? "carregando…" : undefined}
        />
        <StatTile
          label="Concluídas"
          value={completed}
          icon={<CheckCircle2 className="h-4 w-4" />}
          tone="ok"
          foot={total > 0 ? `${Math.round((completed / total) * 100)}% do total` : undefined}
        />
        <StatTile
          label="Em andamento"
          value={inProgress}
          icon={<Clock className="h-4 w-4" />}
          tone="warn"
        />
        <StatTile label="Backlog" value={backlog} icon={<Layers className="h-4 w-4" />} />
        <StatTile
          label="Progresso"
          value={`${progressPct}%`}
          icon={<TrendingUp className="h-4 w-4" />}
          foot={isBehind ? `Tempo decorrido: ${timeElapsedPct}%` : undefined}
          tone={isBehind ? "bad" : "default"}
        />
        <StatTile
          label="Dias restantes"
          value={
            daysLeft === null
              ? "—"
              : daysLeft < 0
                ? `${Math.abs(daysLeft)}d atrasado`
                : `${daysLeft}d`
          }
          icon={<CalendarDays className="h-4 w-4" />}
          tone={daysLeft !== null && daysLeft < 0 ? "bad" : "default"}
        />
      </div>

      {/* Timeline */}
      {project.startDate && project.targetDate && timeElapsedPct !== null && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Linha do tempo</CardTitle>
            <p className="text-xs text-muted-foreground">
              Progresso real vs tempo decorrido — quando o progresso real fica abaixo do tempo
              decorrido, o projeto está atrasado.
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pb-5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Progresso real</span>
                <span className={isBehind ? "font-medium text-destructive" : "font-medium text-ok"}>
                  {progressPct}%
                </span>
              </div>
              <Progress value={progressPct} className="h-2.5" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Tempo decorrido</span>
                <span>{timeElapsedPct}%</span>
              </div>
              <Progress
                value={Math.min(timeElapsedPct, 100)}
                className="h-2.5 bg-muted"
                indicatorClassName="bg-info/60"
              />
            </div>
            <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
              <span>{format(new Date(project.startDate), "dd MMM yyyy", { locale: ptBR })}</span>
              <span className={`font-medium ${isBehind ? "text-destructive" : ""}`}>
                {isBehind
                  ? `⚠ Atrasado ${timeElapsedPct - progressPct}% em relação ao tempo`
                  : "✓ No prazo"}
              </span>
              <span>{format(new Date(project.targetDate), "dd MMM yyyy", { locale: ptBR })}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading state for charts */}
      {issuesLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {issuesError && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Não foi possível carregar as issues deste projeto.
          </CardContent>
        </Card>
      )}

      {!issuesLoading && !issuesError && (
        <>
          {/* Charts row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ChartCard
              title="Issues por Estado"
              subtitle="Distribuição das issues por estado de workflow"
              height={280}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stateData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    label={({ name, value }) => `${name} (${value})`}
                    labelLine={false}
                  >
                    {stateData.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={
                          STATE_TYPE_COLORS[entry.name.toLowerCase()] ??
                          CHART_COLORS[i % CHART_COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => [`${v} issues`, ""]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Issues por Prioridade"
              subtitle="Volumes altos em Urgente e Alta indicam risco no projeto"
              height={280}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priorityData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={95} />
                  <Tooltip formatter={(v: number) => [`${v} issues`, "Quantidade"]} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {priorityData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ChartCard
              title="Carga por Membro"
              subtitle="Issues por responsável — identifica sobrecarga e desequilíbrio no time"
              height={Math.max(240, assigneeData.length * 38 + 40)}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={assigneeData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v: number) => [`${v} issues`, "Quantidade"]} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Velocidade Semanal"
              subtitle="Issues criadas por semana — pico indica entrada de escopo; queda pode indicar bloqueio"
              height={280}
            >
              {velocityData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Dados insuficientes para gráfico de velocidade.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={velocityData} margin={{ left: 0, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => [`${v} issues`, "Criadas"]} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Issues criadas"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Issues table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">
                Issues do projeto
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {filtered.length} de {total}
                </span>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Lista completa de issues com filtros — clique no ícone para abrir no Linear
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Select value={filterState} onValueChange={setFilterState}>
                  <SelectTrigger className="h-8 w-auto gap-1.5 text-xs">
                    <SelectValue placeholder="Todos os estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os estados</SelectItem>
                    {stateNames.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="h-8 w-auto gap-1.5 text-xs">
                    <SelectValue placeholder="Todas as prioridades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as prioridades</SelectItem>
                    {[1, 2, 3, 4, 0].map((p) => (
                      <SelectItem key={p} value={String(p)}>
                        {["Sem prioridade", "Urgente", "Alta", "Média", "Baixa"][p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                  <SelectTrigger className="h-8 w-auto gap-1.5 text-xs">
                    <SelectValue placeholder="Todos os responsáveis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os responsáveis</SelectItem>
                    {assignees.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(filterState !== "all" ||
                  filterPriority !== "all" ||
                  filterAssignee !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={() => {
                      setFilterState("all");
                      setFilterPriority("all");
                      setFilterAssignee("all");
                    }}
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nenhuma issue encontrada com os filtros selecionados.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead className="w-32">Estado</TableHead>
                      <TableHead className="w-28">Prioridade</TableHead>
                      <TableHead className="w-32">Responsável</TableHead>
                      <TableHead className="w-24">Criado</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((issue) => {
                      return (
                        <TableRow key={issue.id}>
                          <TableCell className="font-medium">
                            <span className="line-clamp-1">{issue.title}</span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="gap-1 font-medium"
                              style={{
                                backgroundColor: issue.state.color + "22",
                                color: issue.state.color,
                                borderColor: issue.state.color + "44",
                              }}
                            >
                              <span
                                className="h-1.5 w-1.5 shrink-0 rounded-full"
                                style={{ backgroundColor: issue.state.color }}
                              />
                              {issue.state.name}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              shape="tag"
                              tone={LINEAR_PRIORITY_TONE[issue.priority] ?? "neutral"}
                            >
                              {issue.priorityLabel}
                            </StatusBadge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {issue.assignee?.name ?? "—"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {format(new Date(issue.createdAt), "dd/MM/yy", { locale: ptBR })}
                          </TableCell>
                          <TableCell>
                            <a
                              href={issue.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground transition-colors hover:text-foreground"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
